import React, { useEffect, useState } from 'react';
import { apiGet } from '../../utils/api';
import { buildGagOwnerOptions, gagOwnerPickPatch } from './gagOwnerOptions';
import {
  GAG_STATUSES,
  GAG_STATUS_LABELS,
  TASK_PRIORITIES,
  TASK_PRIORITY_LABELS,
  DAY_NIGHT_OPTIONS,
  INT_EXT_OPTIONS,
} from '../../constants/taskStatuses';
import { GAG_COLUMN_FIELD } from '../../constants/gagTableColumns';
import GagSceneMultiSelect from './GagSceneMultiSelect';
import RenameSyncNote from './RenameSyncNote';
import SuggestInput from '../ui/SuggestInput';
import { scenesToDisplay } from '../../utils/sceneOptions';
import { isMalformedAmount, toPayloadAmount } from '../../utils/moneyPayload';
import { unreadableDaysProblem } from '../../utils/daysEntry';
import { ROW_EDIT_CONTROL } from '../../constants/inputs';

/**
 * GagRowEditCell — the control that replaces one Breakdown table cell while
 * its row is being edited inline.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🚨 THIS COMPONENT DECIDES NOTHING ABOUT PERMISSIONS
 * ═══════════════════════════════════════════════════════════════════════════
 * It renders a control or it renders `null`, and the caller supplies the
 * verdict. There is **no client-side editability test in this file and none
 * may be added** — the same rule `components/OrderInlineEditor.js` carries.
 * The row is only put into edit mode when the SERVER-computed
 * `gag.can_edit === true` (attached per row by `GET /api/gags`), and the Owner
 * control additionally requires `canAssignOwner`, which the page passes
 * straight through from the list payload's `can_manage`.
 *
 * 🔑 **WHY OWNER NEEDS A SECOND FLAG, which is the non-obvious half.**
 * `canEditGag` gates FIELD edits only (`server/routes/gags.js:1316-1319`).
 * `owner_id` on the same `PUT` runs a SEPARATE ladder (`:1321-1341`):
 *   - unowned → self          = take semantics, breakdown rights (2026-10-02)
 *   - owned   → null          = release semantics, owner or manager
 *   - any     → someone else  = **assign semantics, requires
 *                               `canManageBreakdownForDept`**
 *   - plain user claiming an owned gag = 409 "Gag already has an owner"
 * So a non-manager who OWNS a gag has `can_edit: true` and would still be
 * **403'd** on reassignment. Rendering an owner dropdown off `can_edit` alone
 * is precisely the "offer a control the server will refuse" defect — the house
 * rule is to match the client gate to the SERVER gate for the same action.
 *
 * ⚠️ **The visible consequence is deliberate and will look like a bug on first
 * click: a plain owner sees an editable Status and a READ-ONLY Owner in the
 * same row.** That asymmetry is correct. Take and Release remain in the row's
 * kebab menu, where they already work — they are not missing from here, they
 * were never moved.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHAT IS DELIBERATELY NOT EDITABLE
 * ═══════════════════════════════════════════════════════════════════════════
 * RA and Progress carry `editable: false` in `constants/gagTableColumns.js`
 * and are not omissions. `ra_status` is DERIVED from linked risk assessments
 * and `task_done`/`task_total` from child tasks — neither is a column on
 * `gags`, neither appears in the server's `WRITABLE_FIELDS`, and a control for
 * either would write nowhere. Both are edited in `GagDetailPanel`, which stays
 * mounted below the row.
 *
 * 🔀 **SCENES IS EDITABLE HERE SINCE 2026-08-09, AND THE REASON THE PROTOTYPE
 * WITHHELD IT WAS A GOOD ONE — SO SAY WHAT CHANGED RATHER THAN DELETING IT.**
 * This file used to read: *"the `PUT` takes a `scenes` key with its own
 * parse/replace semantics (`parseScenes` → `replaceScenes`), separate from
 * `WRITABLE_FIELDS`. Wiring a bare text input to it without exercising that
 * parser is how a comma-handling bug ships silently."* Every clause of that is
 * still true. What changed is that **the cell is no longer a bare text input**:
 * it is a multi-select over scene numbers harvested from the department
 * (`GET /api/gags/scene-suggestions`), and the parser is now mirrored and
 * case-tabled on both sides (`utils/sceneOptions.js` ↔
 * `server/helpers/sceneNumber.js`). Product owner's ruling; full design in
 * `docs/superpowers/specs/2026-08-09-breakdown-inline-row-editing-design.md`.
 *
 * 🚨 **It is still ONE writer.** The row sends `scenes` on the same diff-only
 * `PUT /api/gags/:gagId` the panel already uses. No second writer of
 * `gag_scenes` was added and none may be.
 *
 * 📱 **`GagDetailPanel` KEEPS ITS SCENES FIELD.** The table is `hidden md:block`
 * and below `md` the same gags render in the PHONE table (2026-10-03; this said
 * "as cards" until 2026-10-18), whose cells are display-only, so the panel
 * is the only scene editor a phone has. Nobody may later "finish the job" by
 * removing it — that would delete scene editing from mobile entirely.
 */

/** Native control chrome — matches the inline Assign picker already in this table.
 *  🔀 Shared since 2026-10-25 (`constants/inputs.js#ROW_EDIT_CONTROL`), so the
 *  Manage Budgets tables edit their rows in the same controls. */
const CONTROL = ROW_EDIT_CONTROL;

/**
 * Read the CURRENT value of a column for the draft, as a form-control string.
 *
 * 🪤 **Everything here is a STRING, including `sub_department_id` and
 * `owner_id`, which are NUMBERS on the gag row.** A `<select>`'s `value` is
 * always a string, so comparing a draft against the raw gag row with `!==`
 * would report every numeric field dirty on open. `buildGagRowPatch` below is
 * the one place that converts back, and `gagRowDraftFrom` is the one place
 * that converts in — the two are inverses and must stay so.
 *
 * 🔑 **`scenes` is MULTI-VALUED and is STILL a string here — deliberately.** It
 * is the comma-joined display form, which is exactly what
 * `PUT /api/gags/:gagId` accepts and what `gags.scenes_display` already is (the
 * panel's own form does the same, `GagDetailPanel.js:55`). An array-valued
 * `scenes` would have broken the all-strings invariant for ONE column, which is
 * the kind of exception that gets forgotten — and it would have needed a
 * bespoke equality test in the diff. `GagSceneMultiSelect` converts in and out.
 *
 * ⚠️ It is normalised through `scenesToDisplay` on the way IN as well as out.
 * `scenes_display` is built by the server and the draft is built by the client,
 * so putting both through one function is what stops an ordering or
 * whitespace disagreement between them reading as a user edit.
 */
export function gagRowDraftFrom(gag) {
  return {
    title: gag.title == null ? '' : String(gag.title),
    status: gag.status == null ? '' : String(gag.status),
    scenes: scenesToDisplay(gag.scenes_display),
    sub_department: gag.sub_department_id == null ? '' : String(gag.sub_department_id),
    shoot: gag.shoot_date == null ? '' : String(gag.shoot_date).slice(0, 10),
    unit: gag.unit == null ? '' : String(gag.unit),
    day_night: gag.day_night == null ? '' : String(gag.day_night),
    int_ext: gag.int_ext == null ? '' : String(gag.int_ext),
    location: gag.location == null ? '' : String(gag.location),
    owner: gag.owner_id == null ? '' : String(gag.owner_id),
    priority: gag.priority == null ? '' : String(gag.priority),
  };
}

/**
 * Build the DIFF-ONLY `PUT /api/gags/:gagId` body from a draft.
 *
 * 🔑 **DIFF-ONLY IS THE ENDPOINT'S CONTRACT, NOT AN OPTIMISATION.** The
 * handler treats "field present in body" as "caller is setting this field"
 * (`routes/gags.js:1305`), and `hasFieldEdits` — which decides whether
 * `canEditGag` is even consulted — is `WRITABLE_FIELDS.some(f => f in
 * req.body)`. Sending every field on every save would make a pure owner change
 * demand field-edit rights it does not need.
 *
 * 🪤 **`''` becomes `null`, never `''`.** The whole codebase treats an absent
 * optional value as `null`, and `normalizeGagField` maps a blank string to
 * `null` for the text fields anyway — but `sub_department_id` and `owner_id`
 * would arrive as the string `''`, which `parseInt` turns into `NaN`.
 *
 * 🔀 **`title` WAS SKIPPED WHEN BLANK UNTIL 2026-09-29; it is now sent as `''`
 * and the unified save refuses it client-side** (`gagDraftSaveError`, the
 * panel's `'Title is required'` — coordinator ruling when the row and panel
 * drafts became one). The old skip left a cleared title on screen, unsaved and
 * unexplained. Nothing is lost: a refused save keeps every edit in the draft.
 *
 * 🪤 **`scenes` is the exception: it is
 * compared through a normaliser and it may legitimately be sent EMPTY.**
 * Clearing every scene is a real edit — `''` reaches `replaceScenes`, which
 * deletes the gag's `gag_scenes` rows — so it must NOT be collapsed to `null`
 * the way a blank text column is, and it must not be skipped the way a blank
 * title is. Both sides of its comparison go through `scenesToDisplay`, so only
 * a change to the SET registers; a reorder cannot. (`parseScenes` treats a
 * non-string as empty, so `null` would in fact work — `''` is sent because it
 * is what the endpoint documents and what the panel already sends.)
 *
 * @returns {object} the request body — `{}` when nothing changed.
 */
export function buildGagRowPatch(gag, draft) {
  const original = gagRowDraftFrom(gag);
  const body = {};
  for (const [colKey, field] of Object.entries(GAG_COLUMN_FIELD)) {
    if (!(colKey in draft)) continue;
    const next = draft[colKey];
    if (colKey === 'scenes') {
      const nextDisplay = scenesToDisplay(next);
      if (nextDisplay === original.scenes) continue;
      body.scenes = nextDisplay;
      continue;
    }
    if (colKey === 'title') {
      // 🔀 2026-09-29 (one draft for row + panel): a CLEARED title is a change,
      // sent as '' so the unified save REFUSES it by name ('Title is
      // required', `gagDraftSaveError`) instead of the row's old silent skip —
      // a skip left the user's edit on screen, unsaved, with no word why.
      // Compared TRIMMED so a trailing space is not an edit (the panel's rule).
      const t = String(next).trim();
      if (t === String(original.title).trim()) continue;
      body.title = t;
      continue;
    }
    if (next === original[colKey]) continue;
    if (field === 'sub_department_id' || field === 'owner_id') {
      body[field] = next === '' ? null : parseInt(next, 10);
    } else {
      body[field] = next === '' ? null : next;
    }
  }
  return body;
}

/**
 * Is this draft different from the gag it was seeded from?
 *
 * Derived from `buildGagRowPatch` rather than computed a second way — a dirty
 * test that can disagree with the thing it guards is worse than none.
 */
export function isGagRowDirty(gag, draft) {
  return Object.keys(buildGagRowPatch(gag, draft)).length > 0;
}

/* ═══════════════════════════════════════════════════════════════════════════
 * ONE DRAFT FOR THE ROW AND THE PANEL (2026-09-29). Owner: *"In expanded gag
 * details if I change the assigned to name it should also show in the main gag
 * row as well then save locks in the changes."*
 *
 * The expanded row's inline editor and `GagDetailPanel`'s editor were two
 * independent drafts over the SAME eleven fields (every row column is also a
 * panel field). `GagGroupTable` now owns ONE draft for the open gag — the row
 * shape above (column keys, all strings) EXTENDED with the panel-only fields —
 * and hands the panel a view of it (`panelFormFromDraft`) plus a writer
 * (`draftPatchFromPanelForm`). So an edit in either place is the same state,
 * there is one dirty test (`isGagDraftDirty`), and one save builds one diff-only
 * PUT (`buildGagDraftPatch`). The panel keeps its own form only when mounted
 * WITHOUT a shared draft (the Timeline's inline panel; a row the server did
 * not mark `can_edit`).
 * ═════════════════════════════════════════════════════════════════════════ */

/** The panel-only editable fields (panel form names, which are the gag's). */
export const GAG_PANEL_ONLY_FIELDS = [
  'description', 'estimated_days', 'estimated_cost', 'primary_effect_type',
  'location_access_date', 'prep_date', 'strike_date', 'requires_risk_assessment',
];

const dateOnlyStr = (v) => (v ? String(v).slice(0, 10) : '');

/** The full shared draft: the row columns plus the panel-only fields, all strings. */
export function gagDraftFrom(gag) {
  return {
    ...gagRowDraftFrom(gag),
    description: gag.description == null ? '' : String(gag.description),
    estimated_days: gag.estimated_days == null ? '' : String(gag.estimated_days),
    estimated_cost: gag.estimated_cost == null ? '' : String(gag.estimated_cost),
    primary_effect_type: gag.primary_effect_type == null ? '' : String(gag.primary_effect_type),
    location_access_date: dateOnlyStr(gag.location_access_date),
    prep_date: dateOnlyStr(gag.prep_date),
    strike_date: dateOnlyStr(gag.strike_date),
    requires_risk_assessment: Number(gag.requires_risk_assessment) ? '1' : '0',
  };
}

/**
 * The diff-only PUT body for the whole draft. Row columns follow
 * `buildGagRowPatch`; panel-only fields are sent as the panel always sent them
 * (the raw form string — the server normalises), except `estimated_cost`,
 * which goes through `toPayloadAmount` exactly as `GagDetailPanel#saveGag` did.
 */
export function buildGagDraftPatch(gag, draft) {
  const body = buildGagRowPatch(gag, draft);
  const original = gagDraftFrom(gag);
  for (const f of GAG_PANEL_ONLY_FIELDS) {
    if (!(f in draft) || draft[f] === original[f]) continue;
    body[f] = f === 'estimated_cost' ? toPayloadAmount(draft[f], null) : draft[f];
  }
  return body;
}

export function isGagDraftDirty(gag, draft) {
  return Object.keys(buildGagDraftPatch(gag, draft)).length > 0;
}

/**
 * Why this draft may not be saved, or `null`. Runs whichever Save was pressed
 * (coordinator ruling 2026-09-29): a row Save must never send — and so ERASE —
 * an unsaved malformed panel cost, and a cleared title is refused by name.
 */
export function gagDraftSaveError(draft) {
  if (String(draft.title ?? '').trim() === '') return 'Title is required';
  if (isMalformedAmount(draft.estimated_cost)) {
    return 'Estimated cost must be a plain number — for example 1000, not 1,000.';
  }
  // A typo in the panel's Estimated days must not ERASE a stored estimate either (input audit #6).
  const days = unreadableDaysProblem('Estimated days', draft.estimated_days);
  if (days) return days;
  return null;
}

/**
 * Rebase a draft onto a new base: a field the user has NOT touched (still equal
 * to the old base) takes the new value; an edited field keeps the edit. The
 * open-row rebase (a fresh server gag) and the post-save re-seed both use it.
 */
export function rebaseGagDraft(draft, oldBaseDraft, newBaseDraft) {
  const next = { ...draft };
  for (const k of Object.keys(newBaseDraft)) {
    if (draft[k] === oldBaseDraft[k]) next[k] = newBaseDraft[k];
  }
  return next;
}

// Column key → panel form key, for the three that differ (`GAG_COLUMN_FIELD`).
const FORM_KEY = (k) => GAG_COLUMN_FIELD[k] || k;
const DRAFT_KEY = Object.fromEntries(Object.entries(GAG_COLUMN_FIELD).map(([col, f]) => [f, col]));

/** The draft as `GagDetailPanel`'s form shape (`sub_department_id`, `owner_id`, `shoot_date`). */
export function panelFormFromDraft(draft) {
  const form = {};
  for (const [k, v] of Object.entries(draft)) form[FORM_KEY(k)] = v;
  return form;
}

/**
 * The gag as it stands once `draft` is saved — the inverse of `gagDraftFrom`
 * (`gagDraftFrom(gagWithDraftApplied(g, d))` equals `d`, title trimmed). The
 * post-save base when a PUT's echo is not a usable gag row.
 */
export function gagWithDraftApplied(gag, draft) {
  const out = { ...gag };
  for (const [f, v] of Object.entries(panelFormFromDraft(draft))) {
    const key = f === 'scenes' ? 'scenes_display' : f;
    out[key] = v === '' ? null : v;
  }
  if (out.title != null) out.title = String(out.title).trim();
  return out;
}

/** A panel form (or a partial one) back into draft keys. */
export function draftPatchFromPanelForm(form) {
  const patch = {};
  for (const [k, v] of Object.entries(form)) patch[DRAFT_KEY[k] || k] = v;
  return patch;
}

/**
 * Does this column offer a control to this caller?
 *
 * 🪤 **THIS PREDICATE EXISTS BECAUSE A COMPONENT THAT RENDERS `null` IS STILL
 * A TRUTHY ELEMENT.** The row wants `{editCell(key) || <ReadOnlyCell/>}`, and
 * `<GagRowEditCell …/>` is an object whether or not the function later returns
 * `null` — so testing the ELEMENT would make every withheld control swallow
 * its own read-only fallback and render an empty cell. The caller must know
 * BEFORE constructing the element, which is what this answers.
 *
 * It is NOT a permission decision: `canAssignOwner` is supplied by the caller
 * from the server's `can_manage`, exactly as `GagRowEditCell` receives it.
 */
export function hasGagRowControl(columnKey, { canAssignOwner = false } = {}) {
  if (!(columnKey in GAG_COLUMN_FIELD)) return false;
  if (columnKey === 'owner') return canAssignOwner === true;
  return true;
}

/**
 * The PANEL form fields (`GagDetailPanel`'s names) the editing row renders a
 * control for — so the panel can leave them out under that row (owner
 * 2026-10-18: "editing directly in the table row for table row items and the
 * Gag Details section will be for additional details"; 11 fields editable in
 * both places was "confusing").
 *
 * 🔑 DERIVED from `hasGagRowControl` over `GAG_COLUMN_FIELD` — no second list,
 * so a column made editable later drops out of the panel automatically, and a
 * withheld control (Owner, for a non-manager) stays IN the panel.
 *
 * 📱 Only meaningful where the row HAS controls: the desktop table. The phone
 * table's cells are display-only, so the caller must not pass this there — the
 * panel is the only phone editor ("PANEL CAN NEVER SHRINK", gags/CLAUDE.md).
 */
export function gagRowControlledPanelFields({ canAssignOwner = false } = {}) {
  const out = new Set();
  for (const colKey of Object.keys(GAG_COLUMN_FIELD)) {
    if (hasGagRowControl(colKey, { canAssignOwner })) out.add(FORM_KEY(colKey));
  }
  return out;
}

/**
 * The Unit cell's suggestions — the `/api/gags/units` list in
 * `ui/SuggestInput` (a `<datalist>` until 2026-10-19, whose ▾ the owner found
 * empty on a filled box — see SuggestInput's docblock for what was and was not
 * observed). Mounted
 * only in the EDITING row's Unit cell (single-expand), and it fetches ON
 * MOUNT. While this cell owns Unit the panel below hides its own Unit box and
 * does NOT fetch (2026-10-19), so an opened row costs one request. The list
 * PORTALS — this `TD` clips.
 */
function UnitInput({ gag, value, onChange, className, ariaLabel }) {
  const [units, setUnits] = useState([]);
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await apiGet('/api/gags/units');
        if (!res.ok) return;
        const d = await res.json();
        if (live) setUnits(Array.isArray(d) ? d : []);
      } catch { /* suggestions are best-effort, as in the panel */ }
    })();
    return () => { live = false; };
  }, []);
  return (
    <SuggestInput value={value} onChange={onChange} options={units}
      inputClassName={className} ariaLabel={ariaLabel} listLabel="Units"
      testId={`gag-edit-unit-${gag.id}`}
      // Owner 2026-10-19, "ONE ENTER SAVES": type, Enter → the list closes and
      // the row saves in the same press (as before the datalist went).
      enterPassesThrough />
  );
}

export default function GagRowEditCell({
  columnKey,
  gag,
  draft,
  onField,
  deptUsers = [],
  subDepts = [],
  sceneSuggestions = [],
  canAssignOwner = false,
  // The viewer's user id (int; may be NaN) — the owner list's "(me)" / "Me".
  userId = NaN,
}) {
  const value = draft[columnKey] ?? '';
  const set = (v) => onField(columnKey, v);
  // Every control shares one label convention so the row is navigable by
  // screen reader: the column heading is not programmatically associated with
  // the cell, so each control names itself and the gag it belongs to.
  const aria = (name) => `${name} for ${gag.title}`;

  switch (columnKey) {
    case 'title':
      // A FRAGMENT, so the `w-full` input stays a direct child of the cell. M-8: a gag with an
      // ACTIVE job code shares its name with the Budget (`has_job_code`, server-computed).
      return (
        <>
          <input type="text" className={CONTROL} value={value} aria-label={aria('Title')}
            data-testid={`gag-edit-title-${gag.id}`}
            onChange={(e) => set(e.target.value)} />
          {gag.has_job_code === true && (
            <RenameSyncNote where="Budget" testId={`gag-title-sync-note-${gag.id}`}
              changed={String(value).trim() !== String(gag.title || '').trim()} />
          )}
        </>
      );

    case 'status':
      // 🪤 `ready` is the STORED value; "Ready to Shoot" is only the LABEL.
      // Options come from GAG_STATUSES + GAG_STATUS_LABELS so the two can
      // never drift. No workflow gate — any transition is allowed and the
      // server validates membership only.
      return (
        <select className={CONTROL} value={value} aria-label={aria('Status')}
          data-testid={`gag-edit-status-${gag.id}`}
          onChange={(e) => set(e.target.value)}>
          {GAG_STATUSES.map((s) => (
            <option key={s} value={s}>{GAG_STATUS_LABELS[s] || s}</option>
          ))}
        </select>
      );

    case 'priority':
      return (
        <select className={CONTROL} value={value} aria-label={aria('Priority')}
          data-testid={`gag-edit-priority-${gag.id}`}
          onChange={(e) => set(e.target.value)}>
          {TASK_PRIORITIES.map((p) => (
            <option key={p} value={p}>{TASK_PRIORITY_LABELS[p] || p}</option>
          ))}
        </select>
      );

    case 'scenes':
      // 🔑 Options are HARVESTED from the department's existing gags, never a
      // configured list — there is no master scene table. The control converts
      // between the chip selection and the comma-joined string the draft (and
      // the PUT) carries; it decides nothing about permissions.
      return (
        <GagSceneMultiSelect
          gag={gag}
          value={value}
          onChange={set}
          suggestions={sceneSuggestions}
        />
      );

    case 'sub_department':
      return (
        <select className={CONTROL} value={value} aria-label={aria('Sub-department')}
          data-testid={`gag-edit-subdept-${gag.id}`}
          onChange={(e) => set(e.target.value)}>
          <option value="">None</option>
          {subDepts.map((sd) => <option key={sd.id} value={sd.id}>{sd.name}</option>)}
        </select>
      );

    case 'shoot':
      return (
        <input type="date" className={CONTROL} value={value} aria-label={aria('Shoot date')}
          data-testid={`gag-edit-shoot-${gag.id}`}
          onChange={(e) => set(e.target.value)} />
      );

    case 'unit':
      return <UnitInput gag={gag} value={value} onChange={set} className={CONTROL} ariaLabel={aria('Unit')} />;

    case 'location':
      return (
        <input type="text" className={CONTROL} value={value} aria-label={aria('Location')}
          data-testid={`gag-edit-location-${gag.id}`}
          onChange={(e) => set(e.target.value)} />
      );

    case 'day_night':
      return (
        <select className={CONTROL} value={value} aria-label={aria('Day or night')}
          data-testid={`gag-edit-daynight-${gag.id}`}
          onChange={(e) => set(e.target.value)}>
          <option value="">-</option>
          {DAY_NIGHT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );

    case 'int_ext':
      return (
        <select className={CONTROL} value={value} aria-label={aria('Interior or exterior')}
          data-testid={`gag-edit-intext-${gag.id}`}
          onChange={(e) => set(e.target.value)}>
          <option value="">-</option>
          {INT_EXT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );

    case 'owner':
      // 🚨 THE ONE PLACE A CONTROL IS WITHHELD FROM SOMEONE WHO MAY EDIT THE
      // ROW. Returning `null` renders NO control — never a disabled one. A
      // disabled input still advertises an action the server refuses, and the
      // brief for this work is explicit that a viewer without rights must see
      // nothing at all. The caller falls back to the read-only cell.
      if (!canAssignOwner) return null;
      return ownerSelect();
    default:
      return null;
  }

  function ownerSelect() {
    // 🔀 2026-10-18: the panel's OWN rule (`gagOwnerOptions.js`, shared with
    // `OwnerSubDeptFields`) — filtered by the row's sub-department, the
    // persisted owner and the held value keep-listed ("(other sub-dept)" on a
    // mismatch), "Me" for a manager missing from the list (a site admin), and
    // picking an owner fills a BLANK sub-department. It replaced an unfiltered
    // `deptUsers` list. 🪤 Keep-listing the HELD value still matters: the
    // draft is SHARED with the panel, and a `<select>` whose value has no
    // `<option>` silently SHOWS "Unassigned", contradicting the panel.
    // `selfClaimAllowed` = `canAssignOwner`, as the panel passes `can_manage`.
    const subDeptValue = draft.sub_department ?? '';
    const options = buildGagOwnerOptions({
      users: deptUsers,
      canManage: canAssignOwner,
      userId,
      subDeptValue,
      ownerValue: value,
      currentOwner: gag.owner_id != null
        ? { id: gag.owner_id, name: gag.owner_name || `User ${gag.owner_id}` }
        : null,
      selfClaimAllowed: canAssignOwner,
    });
    const onOwner = (e) => {
      const patch = gagOwnerPickPatch({ ownerValue: e.target.value, subDeptValue, users: deptUsers });
      onField('owner', patch.owner_id);
      if (patch.sub_department_id != null) onField('sub_department', patch.sub_department_id);
    };
    return (
      <select className={CONTROL} value={value} aria-label={aria('Owner')}
        data-testid={`gag-edit-owner-${gag.id}`}
        onChange={onOwner}>
        <option value="">Unassigned</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }
}
